import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, Menu, X, ChevronDown, Box, Layers, Tag, Wrench } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { Button } from "./ui/button";

const Navbar = () => {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProductsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const productLinks = [
    { to: "/configurator", label: "Wood Enclosures", icon: Box, description: "Custom wooden vivariums" },
    { to: "/engineered", label: "Engineered (Budget)", icon: Wrench, description: "OSB3 budget-friendly builds", badge: "BUDGET", badgeColor: "bg-amber-500" },
    { to: "/pvc", label: "PVC Enclosures", icon: Wrench, description: "Durable PVC builds", badge: "OUT OF STOCK", badgeColor: "bg-red-500" },
    { to: "/stacking", label: "Stacking Units", icon: Layers, description: "Multi-level setups" },
    { to: "/clearance", label: "Clearance", icon: Tag, description: "Discounted items", badge: "SALE", badgeColor: "bg-green-500" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-panel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" data-testid="navbar-logo">
            <img 
              src="/logo.png" 
              alt="ReptiCraft Logo" 
              className="h-12 w-auto"
            />
            <div className="hidden sm:block">
              <span className="text-xl font-bold font-['Space_Grotesk'] text-[#4CAF50]">REPTI</span>
              <span className="text-xl font-bold font-['Space_Grotesk'] text-[#F4F1DE]">CRAFT</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {/* Products Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setProductsDropdownOpen(!productsDropdownOpen)}
                className="flex items-center gap-1 text-[#F4F1DE]/80 hover:text-[#E07A5F] transition-colors font-medium py-2"
                data-testid="nav-products-dropdown"
              >
                Products
                <ChevronDown className={`w-4 h-4 transition-transform ${productsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {productsDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-[#121212] border border-white/10 rounded-lg shadow-xl overflow-hidden">
                  {productLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="flex items-start gap-3 px-4 py-3 hover:bg-white/5 transition-colors group"
                      onClick={() => setProductsDropdownOpen(false)}
                      data-testid={`nav-${link.to.slice(1)}`}
                    >
                      <link.icon className="w-5 h-5 text-[#E07A5F] mt-0.5" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[#F4F1DE] font-medium group-hover:text-[#E07A5F] transition-colors">
                            {link.label}
                          </span>
                          {link.badge && (
                            <span className={`${link.badgeColor} text-[10px] text-white px-1.5 py-0.5 rounded`}>
                              {link.badge}
                            </span>
                          )}
                        </div>
                        <p className="text-[#F4F1DE]/50 text-xs mt-0.5">{link.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link 
              to="/guides" 
              className="text-[#F4F1DE]/80 hover:text-[#E07A5F] transition-colors font-medium"
              data-testid="nav-guides"
            >
              Care Guides
            </Link>

            <Link 
              to="/about" 
              className="text-[#F4F1DE]/80 hover:text-[#E07A5F] transition-colors font-medium"
              data-testid="nav-about"
            >
              About
            </Link>
            
            <Link 
              to="/contact" 
              className="text-[#F4F1DE]/80 hover:text-[#E07A5F] transition-colors font-medium"
              data-testid="nav-contact"
            >
              Contact
            </Link>
            
            {user ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="text-[#F4F1DE]/80 hover:text-[#E07A5F] transition-colors font-medium"
                  data-testid="nav-dashboard"
                >
                  Dashboard
                </Link>
                <Link 
                  to="/cart" 
                  className="relative text-[#F4F1DE]/80 hover:text-[#E07A5F] transition-colors"
                  data-testid="nav-cart"
                >
                  <ShoppingCart className="w-6 h-6" />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#E07A5F] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                      {cartCount}
                    </span>
                  )}
                </Link>
                <div className="flex items-center space-x-3">
                  <span className="text-[#F4F1DE]/60 text-sm">{user.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="text-[#F4F1DE]/80 hover:text-[#E07A5F]"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-5 h-5" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-3">
                <Link to="/login" data-testid="nav-login">
                  <Button variant="ghost" className="text-[#F4F1DE]/80 hover:text-[#E07A5F]">
                    Login
                  </Button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <Button className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white">
                    Sign Up
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden text-[#F4F1DE]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2">
            {/* Products Section */}
            <div className="border-b border-white/10 pb-4 mb-4">
              <p className="text-[#F4F1DE]/40 text-xs uppercase tracking-wider mb-2 px-2">Products</p>
              {productLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-3 text-[#F4F1DE]/80 hover:text-[#E07A5F] py-2 px-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <link.icon className="w-5 h-5 text-[#E07A5F]" />
                  <span>{link.label}</span>
                  {link.badge && (
                    <span className={`${link.badgeColor} text-[10px] text-white px-1.5 py-0.5 rounded ml-auto`}>
                      {link.badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>

            <Link 
              to="/guides" 
              className="block text-[#F4F1DE]/80 hover:text-[#E07A5F] py-2 px-2"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-nav-guides"
            >
              Care Guides
            </Link>
            <Link 
              to="/about" 
              className="block text-[#F4F1DE]/80 hover:text-[#E07A5F] py-2 px-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              About
            </Link>
            <Link 
              to="/contact" 
              className="block text-[#F4F1DE]/80 hover:text-[#E07A5F] py-2 px-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              Contact
            </Link>
            
            {user ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="block text-[#F4F1DE]/80 hover:text-[#E07A5F] py-2 px-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <Link 
                  to="/cart" 
                  className="block text-[#F4F1DE]/80 hover:text-[#E07A5F] py-2 px-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Cart ({cartCount})
                </Link>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="block text-[#F4F1DE]/80 hover:text-[#E07A5F] py-2 px-2 w-full text-left"
                >
                  Logout
                </button>
              </>
            ) : (
              <div className="border-t border-white/10 pt-4 mt-4 space-y-2">
                <Link 
                  to="/login" 
                  className="block text-[#F4F1DE]/80 hover:text-[#E07A5F] py-2 px-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="block text-[#E07A5F] font-medium py-2 px-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
